import {
  TimelineViewModelState,
  TimelineViewSettings,
  TimelineChartPropertiesState,
  ViewRelevantWorkspaceScopeData,
  FieldsMetaData,
  ViewModelSourceData,
  ViewLoadingStatus,
  TimelineViewModelData,
  WorkspaceAndFieldsRegistry,
  VirtualScrollState,
  MouseHoverEventStreamState,
  RenderForExportState,
} from '../types';
import {
  ChartAreaScrollTopPayload,
  CollapseExpandAllGroupsPayload,
  CollapseExpandGroupPayload,
  SetFocusedItemPayload,
  SetHoveredItemPayload,
  SetTimelineChartWindowScrollPositionPayload,
} from '../actions';
import { transformTimelineViewModelDataToRenderingData } from './viewModelDataToRenderingData';
import { getFilteredRenderingDataWithInteractiveNodeStates } from './getFilteredRenderingDataWithInteractiveNodeStates';
import { ChartWindowArgs } from '@ardoq/graph';
import {
  cleanUpCollapsedIds,
  getFieldsRelevantForFilteringBySelectedFields,
  getHoveredFieldsPopoverProps,
  getSelectedRelevantDateFieldsOrder,
  updateAllCollapsed,
  updateCollapsed,
  withWorkspaceNameOnDuplicatedNames,
} from './calculations';
import { getWindowCenterXFromScrollLeft } from '../chart/calculations';
import { DEFAULT_WINDOW_CENTER, POPOVER_ELEMENT_TYPE } from '../consts';
import { calculateCrosshairXProps } from '../xAxis/utils';
import { buildViewModelData } from './buildViewModelData';
import { TimelineField } from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { partition } from 'lodash';
import { applyInteractiveStates } from './applyInteractiveStates';
import { applyCollapsedFieldDataStates } from './applyCollapsedFieldDataStates';
import { getOtherLabelPopoverProps } from './utils';
import {
  clampRowsToVirtualWindow,
  getContainerHeight,
  getVirtualScrollState,
} from '../calculations';

/**
 * Reacts to changes in relevant view settings, and chart parameters, such as dimensions.
 */
const resetChartAndItemsRenderingData = (
  state: TimelineViewModelState,
  [sourceData, timelineChartPropertiesState, chartWindow]: [
    ViewModelSourceData & { timelineViewModelData: TimelineViewModelData },
    TimelineChartPropertiesState,
    ChartWindowArgs,
  ]
): TimelineViewModelState => {
  const { timelineViewModelData, ...viewModelSourceData } = sourceData;

  const newViewSettings = sourceData.sourceDataViewSettings;

  const {
    clientRect,
    userDefinedLabelsColumnWidth,
    getTimelineContentContainer,
  } = timelineChartPropertiesState;

  const chartPropertiesState =
    state.timelineViewRenderingData.timelineChartRenderingData
      .timelineChartProperties;

  const isDataReset = timelineViewModelData !== state.timelineViewModelData;

  /**
   * The rendered collapse state is the authority: clicks update it directly and
   * write through to the view settings.
   */
  const shouldReadCollapsedStateFromSettings =
    isDataReset ||
    newViewSettings.persistedCollapsedState !==
      state.viewSettings.persistedCollapsedState;

  const updatedState: TimelineViewModelState = {
    ...state,
    viewModelSourceData,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      timelineChartRenderingData: {
        ...state.timelineViewRenderingData.timelineChartRenderingData,
        timelineChartProperties: {
          ...chartPropertiesState,
          chartWindow: {
            ...chartPropertiesState.chartWindow,
            ...chartWindow,
          },
          clientRect,
          userDefinedLabelsColumnWidth,
        },
      },
    },
    timelineViewModelData,
    viewSettings: newViewSettings,
    getTimelineContentContainer,
  };

  const isViewEmpty = sourceData.isViewEmpty;
  const isChartContainerRendered = clientRect.width !== 0;

  if (isViewEmpty) {
    return {
      ...updatedState,
      viewStatus: isChartContainerRendered
        ? ViewLoadingStatus.READY
        : ViewLoadingStatus.LOADING,
      collapsedStateOrigin: 'data',
    };
  }

  const {
    domainRange: previousDomainRangeViewSettingsValue,
    gridPrecision: previousGridPrecisionViewSettingsValue,
  } = state.viewSettings;

  const timelineViewRenderingData =
    transformTimelineViewModelDataToRenderingData({
      ...updatedState,
      previousDomainRangeViewSettingsValue,
      previousGridPrecisionViewSettingsValue,
      isDataReset,
      shouldReadCollapsedStateFromSettings,
    });

  /**
   * Disruptive graph layout changes, such as loading slides, can invalidate the collapsed groups state.
   * To avoid potential memory leaks, we do a quick sweep to remove dead ids.
   */
  const viewRenderingDataWithCleanCollapsedState = {
    ...timelineViewRenderingData,
    collapsedGroupIds: cleanUpCollapsedIds(
      timelineViewRenderingData.collapsedGroupIds,
      timelineViewModelData.collapsibleGroups.collapsible
    ),
    collapsedFieldDataIds: cleanUpCollapsedIds(
      timelineViewRenderingData.collapsedFieldDataIds,
      timelineViewModelData.collapsibleFieldDataIds.collapsible
    ),
  };

  const hasRenderedComponents =
    timelineViewRenderingData.allItemsWithRenderingData
      .allItemLabelsWithRenderingData.length > 0;

  const viewStatus =
    hasRenderedComponents && isChartContainerRendered
      ? ViewLoadingStatus.READY
      : ViewLoadingStatus.LOADING;

  return {
    ...updatedState,
    timelineViewRenderingData: viewRenderingDataWithCleanCollapsedState,
    viewStatus,
    collapsedStateOrigin: 'data',
  };
};

const handleCollapsedGroup = (
  state: TimelineViewModelState,
  { rawItemIdPath, isCollapsed }: CollapseExpandGroupPayload
): TimelineViewModelState => {
  const updatedState = {
    ...state,
    collapsedStateOrigin: 'user' as const,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      collapsedGroupIds: updateCollapsed({
        rawItemIdPath,
        collapsedIds: state.timelineViewRenderingData.collapsedGroupIds,
        collapsibleIds:
          state.timelineViewModelData.collapsibleGroups.collapsible,
        isCollapsed,
      }),
    },
  };

  return {
    ...updatedState,
    timelineViewRenderingData: {
      ...updatedState.timelineViewRenderingData,
      ...getFilteredRenderingDataWithInteractiveNodeStates({
        ...updatedState,
      }),
    },
  };
};

const handleAllGroupsCollapsed = (
  state: TimelineViewModelState,
  { isCollapsed }: CollapseExpandAllGroupsPayload
): TimelineViewModelState => {
  const updatedState = {
    ...state,
    collapsedStateOrigin: 'user' as const,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      collapsedGroupIds: updateAllCollapsed({
        collapsibleIds:
          state.timelineViewModelData.collapsibleGroups.collapsible,
        isCollapsed,
      }),
      // As per design request, currently, when user collapses all groups, the same operation should collapse all fields as well.
      collapsedFieldDataIds: updateAllCollapsed({
        collapsibleIds:
          state.timelineViewModelData.collapsibleFieldDataIds.collapsible,
        isCollapsed,
      }),
    },
  };

  return {
    ...updatedState,
    timelineViewRenderingData: {
      ...updatedState.timelineViewRenderingData,
      ...getFilteredRenderingDataWithInteractiveNodeStates({
        ...updatedState,
      }),
    },
  };
};

/**
 * Currently not in use since "collapse all fields" is executed together with groups collapse.
 */
const handleAllFieldDataCollapsed = (
  state: TimelineViewModelState,
  { isCollapsed }: CollapseExpandAllGroupsPayload
): TimelineViewModelState => {
  if (!state.viewModelSourceData.viewInterface.features.isModernizedStyle) {
    return state;
  }

  const updatedState = {
    ...state,
    collapsedStateOrigin: 'user' as const,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      collapsedFieldDataIds: updateAllCollapsed({
        collapsibleIds:
          state.timelineViewModelData.collapsibleFieldDataIds.collapsible,
        isCollapsed,
      }),
    },
  };

  const collapsedFieldDataState = applyCollapsedFieldDataStates({
    ...updatedState,
  });

  return {
    ...updatedState,
    timelineViewRenderingData: {
      ...updatedState.timelineViewRenderingData,
      ...collapsedFieldDataState,
      renderedItemPhases:
        collapsedFieldDataState.itemPhasesWithCollapsedFieldDataState,
      renderedItemLabels:
        collapsedFieldDataState.itemLabelsWithCollapsedFieldDataState,
    },
  };
};

const handleCollapsedFieldData = (
  state: TimelineViewModelState,
  { rawItemIdPath, isCollapsed }: CollapseExpandGroupPayload
): TimelineViewModelState => {
  if (!state.viewModelSourceData.viewInterface.features.isModernizedStyle) {
    return state;
  }

  const updatedState = {
    ...state,
    collapsedStateOrigin: 'user' as const,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      collapsedFieldDataIds: updateCollapsed({
        rawItemIdPath,
        collapsedIds: state.timelineViewRenderingData.collapsedFieldDataIds,
        collapsibleIds:
          state.timelineViewModelData.collapsibleFieldDataIds.collapsible,
        isCollapsed,
      }),
    },
  };

  return {
    ...updatedState,
    timelineViewRenderingData: {
      ...updatedState.timelineViewRenderingData,
      ...getFilteredRenderingDataWithInteractiveNodeStates({
        ...updatedState,
      }),
    },
  };
};

const updateHoveredItemsState = (
  state: TimelineViewModelState,
  hoveredItemIdPayload: SetHoveredItemPayload
): TimelineViewModelState => {
  const hoveredItemId = hoveredItemIdPayload.hoveredItemId;

  const updatedState = {
    ...state,
    hoveredItemId: hoveredItemIdPayload.hoveredItemId,
  };

  return {
    ...state,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      ...applyInteractiveStates({
        ...updatedState,
      }),
    },
    hoveredItemId,
  };
};

const updateFocusedItemsState = (
  state: TimelineViewModelState,
  focusedItemIdPayload: SetFocusedItemPayload
): TimelineViewModelState => {
  const payloadItemId = focusedItemIdPayload.focusedItemId;

  const focusedItemId =
    payloadItemId === state.focusedItemId ? null : payloadItemId;

  const updatedState = {
    ...state,
    focusedItemId,
  };

  return {
    ...state,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      ...applyInteractiveStates({
        ...updatedState,
      }),
    },
    focusedItemId,
  };
};

const updateChartWindowStateBasedOnChartWindowScrollPosition = (
  state: TimelineViewModelState,
  { scrollLeft }: SetTimelineChartWindowScrollPositionPayload
): TimelineViewModelState => {
  const chartPropertiesState =
    state.timelineViewRenderingData.timelineChartRenderingData
      .timelineChartProperties;

  const chartWidth = chartPropertiesState.dimensions.chart.width;
  const windowScale = chartPropertiesState.chartWindow.windowScale;

  const nextWindowCenter: [number, number] = [
    getWindowCenterXFromScrollLeft({
      chartWidth,
      windowScale,
      scrollLeft,
    }),
    DEFAULT_WINDOW_CENTER[1],
  ];

  const updatedState = {
    ...state,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      timelineChartRenderingData: {
        ...state.timelineViewRenderingData.timelineChartRenderingData,
        timelineChartProperties: {
          ...chartPropertiesState,
          chartWindow: {
            windowScale,
            windowCenter: nextWindowCenter,
          },
        },
      },
    },
  };

  return {
    ...updatedState,
    timelineViewRenderingData: transformTimelineViewModelDataToRenderingData({
      ...updatedState,
      preventChartScaleReset: true,
    }),
  };
};
const updateMouseHoverState = (
  state: TimelineViewModelState,
  {
    crosshairXPosition,
    isResizeActive,
    graphNodeId,
    itemPhasesRowIndex,
    otherLabelIndex,
    popoverElementType,
    popoverX,
    popoverY,
  }: MouseHoverEventStreamState
): TimelineViewModelState => {
  const chartPropertiesState =
    state.timelineViewRenderingData.timelineChartRenderingData
      .timelineChartProperties;

  const marginLeft = chartPropertiesState.dimensions.margin.left;
  const chartWidth = chartPropertiesState.dimensions.chart.width;
  const chartAreaWidth = marginLeft + chartWidth;

  const isWithinChartWindow =
    crosshairXPosition >= marginLeft && crosshairXPosition <= chartAreaWidth;

  const nextCrosshairXProps =
    !isResizeActive && isWithinChartWindow
      ? calculateCrosshairXProps({
          crosshairXPosition,
          chartAreaWidth,
          xAxisContainerHeight:
            chartPropertiesState.xAxisProps.xAxisContainerHeight,
          unscaler: chartPropertiesState.unscaleX,
          gridPrecision: chartPropertiesState.adjustedGridPrecision,
        })
      : null;

  const isFieldPopover =
    (popoverElementType === POPOVER_ELEMENT_TYPE.MILESTONE ||
      popoverElementType === POPOVER_ELEMENT_TYPE.PHASE) &&
    itemPhasesRowIndex !== null &&
    isWithinChartWindow &&
    graphNodeId;

  const isMainLabelPopover =
    popoverElementType === POPOVER_ELEMENT_TYPE.MAIN_LABEL;

  const isOtherLabelPopover =
    popoverElementType === POPOVER_ELEMENT_TYPE.OTHER_LABEL &&
    otherLabelIndex !== null;

  const hoveredFieldsPopoverProps = isFieldPopover
    ? getHoveredFieldsPopoverProps({
        hoveredItemFields:
          state.timelineViewRenderingData.renderedPhasesByGraphNodeId[
            graphNodeId
          ],
        itemPhasesRowIndex,
        hoverPosition: crosshairXPosition - marginLeft,
        chartWidth,
        popoverX,
        popoverY,
        adjustedGridPrecision: chartPropertiesState.adjustedGridPrecision,
        i18nLocale: state.viewModelSourceData.i18nLocale,
      })
    : undefined;

  const renderedLabel = graphNodeId
    ? state.timelineViewRenderingData.renderedLabelsByGraphNodeId.get(
        graphNodeId
      )
    : null;

  const mainLabelPopoverContent = renderedLabel?.popoverContent;
  const hoveredMainLabelPopoverProps =
    isMainLabelPopover && mainLabelPopoverContent
      ? {
          popoverY,
          popoverX,
          popoverContent: mainLabelPopoverContent,
        }
      : undefined;

  const otherLabelData = renderedLabel?.otherLabelsRenderingData;
  const hoveredOtherLabelPopoverProps =
    isOtherLabelPopover && otherLabelData && otherLabelIndex !== null
      ? getOtherLabelPopoverProps(
          otherLabelData,
          otherLabelIndex,
          popoverX,
          popoverY
        )
      : undefined;

  return {
    ...state,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      timelineChartRenderingData: {
        ...state.timelineViewRenderingData.timelineChartRenderingData,
        timelineChartProperties: {
          ...chartPropertiesState,
          crosshairXProps: nextCrosshairXProps,
          hoveredFieldsPopoverProps,
          hoveredMainLabelPopoverProps,
          hoveredOtherLabelPopoverProps,
        },
      },
    },
  };
};

type GetFieldDataArgs = {
  viewRelevantWorkspacesScopeData: ViewRelevantWorkspaceScopeData[];
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry;
  viewSettings: TimelineViewSettings;
  selectedFields?: TimelineField[] | undefined;
  isFilterBySelectedFields?: boolean;
};
const getFieldsMetaData = ({
  viewRelevantWorkspacesScopeData,
  viewSettings,
  selectedFields,
  isFilterBySelectedFields,
  workspaceAndFieldsRegistry,
}: GetFieldDataArgs): FieldsMetaData => {
  const allDateFields = viewRelevantWorkspacesScopeData.flatMap(
    ({ relevantFieldsInWorkspace }) => relevantFieldsInWorkspace
  );

  // partition fields into dateRangeFields and milestoneFields
  const [allDateRangeFields, allMilestoneFields] = partition(
    dateRangeOperations.mergeDateTimeFieldsToDateRangeFields(allDateFields)
      .fields,
    dateRangeOperations.isDateRangeField
  );

  // determine what fields are relevant for the view in regard to filtering by selected fields
  // selectedFields is an argument from Discover
  const { relevantMilestoneFields, relevantDateRangeFields } =
    getFieldsRelevantForFilteringBySelectedFields({
      isFilterBySelectedFields,
      selectedFields,
      allDateRangeFields,
      allMilestoneFields,
    });

  // enhance the relevant fields with workspace name labels
  const relevantDateRangeFieldsWithWorkspaceNames =
    withWorkspaceNameOnDuplicatedNames(
      relevantDateRangeFields,
      workspaceAndFieldsRegistry
    );
  const relevantMilestoneFieldsWithWorkspaceNames =
    withWorkspaceNameOnDuplicatedNames(
      relevantMilestoneFields,
      workspaceAndFieldsRegistry
    );

  // determine what fields are selected. This order is used for the legend and menus
  const orderedSelectedMilestoneFields = getSelectedRelevantDateFieldsOrder(
    relevantMilestoneFieldsWithWorkspaceNames,
    viewSettings.selectedMilestoneFields
  );
  const orderedSelectedDateRangeFields = getSelectedRelevantDateFieldsOrder(
    relevantDateRangeFieldsWithWorkspaceNames,
    viewSettings.selectedPhaseFields
  );

  return {
    relevantDateRangeFieldsWithWorkspaceNames,
    relevantMilestoneFieldsWithWorkspaceNames,
    orderedSelectedMilestoneFields,
    orderedSelectedDateRangeFields,
  };
};

const updateVirtualScroll = (
  state: TimelineViewModelState,
  { scrollTop }: ChartAreaScrollTopPayload
): TimelineViewModelState => {
  const {
    timelineViewRenderingData,
    timelineViewModelData,
    hoveredItemId,
    focusedItemId,
  } = state;

  const {
    itemLabelsWithCollapsedFieldDataState,
    itemPhasesWithCollapsedFieldDataState,
    allItemTopsWithSentinel,
    virtualScrollState: currentVirtualScrollState,
  } = timelineViewRenderingData;

  if (!itemLabelsWithCollapsedFieldDataState.length) {
    return state;
  }

  const { visibleAreaHeight } = getContainerHeight({
    timelineChartRenderingData:
      timelineViewRenderingData.timelineChartRenderingData,
    allItemTopsWithSentinel,
    backgroundScrollHeight: timelineViewRenderingData.backgroundScrollHeight,
  });

  // We need to render all items when exporting, wait for the export
  // to finish and resume the virtualization again.
  const nextVirtualScrollState: VirtualScrollState = state.isRenderingForExport
    ? {
        firstItemIndex: 0,
        lastItemIndex: itemLabelsWithCollapsedFieldDataState.length - 1,
        scrollTop,
      }
    : getVirtualScrollState({
        chartHeight: visibleAreaHeight,
        scrollTop,
        currentIndex: currentVirtualScrollState.firstItemIndex,
        itemTops: allItemTopsWithSentinel,
      });

  const {
    labelsInVirtualWindow,
    phasesInVirtualWindow,
    itemTopsInVirtualWindow,
  } = clampRowsToVirtualWindow({
    allLabels: itemLabelsWithCollapsedFieldDataState,
    allPhases: itemPhasesWithCollapsedFieldDataState,
    virtualScrollState: nextVirtualScrollState,
    allItemTopsWithSentinel,
  });

  const { renderedItemLabels, renderedItemPhases } = applyInteractiveStates({
    timelineViewRenderingData: {
      labelsInVirtualWindow,
      phasesInVirtualWindow,
    },
    timelineViewModelData,
    hoveredItemId,
    focusedItemId,
  });

  return {
    ...state,
    timelineViewRenderingData: {
      ...state.timelineViewRenderingData,
      virtualScrollState: nextVirtualScrollState,
      labelsInVirtualWindow,
      phasesInVirtualWindow,
      itemTopsInVirtualWindow,
      renderedItemLabels,
      renderedItemPhases,
    },
  };
};

const updateRenderForExportState = (
  state: TimelineViewModelState,
  { renderingForExport }: RenderForExportState
): TimelineViewModelState => {
  const newState = {
    ...state,
    isRenderingForExport: renderingForExport,
  };

  return updateVirtualScroll(newState, {
    scrollTop: 0,
  });
};

export const timelineViewModelOperations = {
  resetChartAndItemsRenderingData,
  handleCollapsedGroup,
  handleAllGroupsCollapsed,
  updateHoveredItemsState,
  updateFocusedItemsState,
  updateChartWindowStateBasedOnChartWindowScrollPosition,
  updateMouseHoverState,
  buildViewModelData,
  getFieldsMetaData,
  handleAllFieldDataCollapsed,
  handleCollapsedFieldData,
  updateVirtualScroll,
  updateRenderForExportState,
};
